import AudioPreview from 'components/UI/AudioPreview';
import { default as TextToggle, default as TextToggle } from 'components/UI/TextToggle';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { usePerms } from 'contexts/PermsContext';
import { default as layout, default as layout } from 'data/DeviceControls.json';
import Hls from 'hls.js';
import useSirenAudio from 'hooks/useSirenAudio';
import { useEffect, useEffect, useRef, useRef, useState, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import { IoVolumeHigh, IoVolumeMute } from 'react-icons/io5';
import { PiPencilLineFill } from 'react-icons/pi';

/**
 * @param {{device: object, displayMessage?: {type: string, message: string}, on?: (event: string, data: object), sending?: boolean}}
 */
export default function DeviceControls({
  device,
  devices,
  displayMessage,
  audioOverride,
  on,
  sending = null,
  successMsg,
  errorMsg,
  audioManagerButtonEnabled = true,
}) {
  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      require('hooks/useRenderTracker').default('DeviceControls');
    }
  } catch (e) {}
  const { universalAudio, sirenAudio } = useSirenAudio(devices || device);
  const perms = usePerms();

  const audioRef = useRef(null);
  const hlsRef = useRef(null);

  const [type, setType] = useState('');
  const [changes, setChanges] = useState(false);
  const [removeMe, setRemoveMe] = useState(false);
  const [audioList, setAudioList] = useState(null);
  const [liveAudio, setLiveAudio] = useState(false);

  // Track changes only for user-initiated edits (avoid false "unsaved" on init).
  const setData = (newState, trackChanges = true) => {
    on('update', newState);
    if (trackChanges) setChanges(true);
  };

  useEffect(() => {
    on('open');

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      on('close');
    };
  }, []);

  useEffect(() => {
    if (!device && !devices) return;

    //TODO: To enable custom audio to group dropdown replace with
    //setAudioList([...universalAudio, ...sirenAudio]);
    setAudioList(devices ? [...universalAudio] : [...universalAudio, ...sirenAudio]);
  }, [universalAudio, sirenAudio]);

  useEffect(() => {
    if (!device && !devices) return;
    if (audioOverride)
      setData(
        {
          ...device,
          status: audioOverride?.relay_id || true,
          data: [{ ...(device?.data?.[0] || {}), status: 1 }],
          audioIndex: -1,
          audio: audioOverride,
        },
        false,
      );
    else
      setData(
        {
          ...device,
          status: false,
          data: [{ ...(device?.data?.[0] || {}), status: 1 }],
          audioIndex: null,
          audio: null,
        },
        false,
      );
  }, [audioOverride]);

  useEffect(() => {
    if (!device && !devices) return;
    setType(device?.device_type || '');

    let newDevice = { ...device };
    let changed = false;

    if (device?.status === undefined || device?.toggle_switch_text === undefined) {
      newDevice = {
        ...newDevice,
        toggle_switch_text: device?.data?.[0]?.toggle_switch ? 'On' : 'Off',
        status: device?.status || !!device?.data?.[0]?.status,
      };
      changed = true;
    }

    if (
      device?.data?.[0]?.command > 0 &&
      device?.device_type.includes('Siren') &&
      device?.audioIndex === undefined &&
      audioList
    ) {
      const audio = audioList.findIndex((audio) => audio.relay_id === device?.data?.[0].command);

      if (audio) {
        newDevice = {
          ...newDevice,
          audioIndex: audio,
          audio: audioList[audio],
        };
        changed = true;
      }
    }

    if (changed) setData(newDevice, false);
  }, [device]);

  const toggleSirenLiveAudio = async () => {
    if (!!devices) return;

    if (liveAudio) stopLiveAudio();
    else startLiveAudio();
  };

  const startLiveAudio = () => {
    const url = `${process.env.PUBLIC_API_URL}/audio/${device?.id}/hls/index.m3u8`;

    const hls = new Hls();
    hlsRef.current = hls;
    hls.loadSource(url);
    hls.attachMedia(audioRef.current);
    hls.on(Hls.Events.ERROR, (_event, data) => {
      console.error('Error in DeviceControls:Hls:', data);
    });
    setLiveAudio(true);
  };

  const stopLiveAudio = () => {
    hlsRef.current?.destroy();
    hlsRef.current = null;

    audioRef.current?.pause();
    audioRef.current?.removeAttribute('src');
    audioRef.current?.load();

    setLiveAudio(false);
  };

  const renderComponent = (component) => {
    switch (component.type) {
      case 'Label':
        return renderLabel(component);
      case 'TextToggle':
        return renderTextToggle(component);
      case 'AudioTextToggle':
        return renderSirenTextToggle(component);
      case 'textarea':
        return renderTextArea(component);
      case 'interval-input':
        return renderIntervalInput(component);
      case 'slider':
        return renderSlider(component);
      case 'send-status-to':
        return renderExternalShare();
      case 'unsaved-changes-label':
        return renderUnsavedChanges();
      case 'save-button':
        return renderSaveButton();
      case 'audio-dropdown':
        return renderAudioDropdown();
      case 'maintenance-override':
        return renderMaintenanceOverride();
      case 'display-message':
        return renderDisplayMessage();
      default:
        return null;
    }
  };

  const renderLabel = (component) => {
    const label = component.label.replace(/{(.*?)}/g, (_, key) => {
      return device[key] ?? '';
    });
    return (
      <div>
        <label>{label}</label>
        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderTextToggle = (component) => {
    let properties = {
      disabled:
        (device?.kill_switch || device?.data?.[0]?.status < 0) && !device?.maintenanceOverride,
      options: component.props.options,
      value: !!device[component.props.value],
      setValue: (value) => {
        if (component.props.setValue === 'status')
          setData({
            ...device,
            [component.props.setValue]: value,
            data: [{ ...(device?.data?.[0] || {}), status: value ? 1 : 0 }],
          });
        else
          setData({
            ...device,
            [component.props.setValue]: value,
          });
      },
    };

    return (
      <div>
        <div className="d-flex flex-column flex-lg-row justify-content-between">
          <div className="flex-fill flex-bases-0">
            <label>{component.label}</label>
          </div>
          <div className="flex-fill flex-bases-0">
            <TextToggle {...properties} />
          </div>
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderSirenTextToggle = (component) => {
    let properties = {
      disabled:
        (device?.kill_switch || device?.data?.[0]?.status < 0) && !device?.maintenanceOverride,
      options: component.props.options,
      value: !!device[component.props.value],
      setValue: (value) => {
        if (component.props.setValue === 'status')
          setData({
            ...device,
            [component.props.setValue]: value,
            data: [{ ...(device?.data?.[0] || {}), status: value ? 1 : 0 }],
          });
        else
          setData({
            ...device,
            [component.props.setValue]: value,
          });
      },
    };

    return (
      <div>
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-stretch align-items-lg-center gap-2 gap-lg-0">
          <div className="d-flex flex-column flex-lg-row flex-fill flex-basis-0 justify-content-between align-items-stretch align-items-lg-center">
            <div className="flex-fill flex-basis-0">
              <label>{component.label}</label>
            </div>
            <div className="flex-fill">
              <TextToggle {...properties} />
            </div>
          </div>

          <div className="d-flex flex-fill flex-basis-0 justify-content-lg-end gap-2">
            {!devices && device?.camera && (
              <button className="btn btn-primary" onClick={toggleSirenLiveAudio}>
                {!liveAudio && <IoVolumeMute size={25} />}
                {liveAudio && <IoVolumeHigh size={25} />}
              </button>
            )}

            <button className="btn btn-primary" onClick={() => on('cancel-audio', {})}>
              Cancel Audio
            </button>
          </div>
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderTextArea = (component) => {
    return (
      <div>
        <div className="d-flex flex-column flex-lg-row align-items-baseline gap-2">
          <label>{component?.label}</label>
          <textarea
            className="border border-white rounded"
            placeholder={component?.props?.placeholder}
            onChange={(e) =>
              setData({
                ...device,
                [component?.props?.onChange]: e.target.value,
              })
            }
          />
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderIntervalInput = (component) => {
    if (!device?.status) return null;

    const {
      text,
      disableValue,
      maxValue,
      minValue,
      value,
      repeatValue,
      repeatForeverLabel,
      repeatForeverValue,
    } = component.props;

    return (
      <div>
        <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-2">
          <button onClick={() => setRemoveMe(!removeMe)}>
            <div className="d-flex gap-1 align-items-center">
              <FaInfoCircle /> <label>{component?.label}</label>
            </div>

            <div
              className={`${
                removeMe ? 'd-block' : 'd-none'
              } position-absolute bg-white text-black rounded p-2 mw-250px z-2`}
            >
              <p>
                This setting controls how often the event should automatically repeat, measured in
                seconds. It's especially useful for scenarios where an action, such as a siren
                activation or audible alert, needs to occur on a recurring loop. For example, if
                you're triggering a 30-second siren tone, you can set it to repeat continuously
                every 30 seconds, every 15 minutes, or even once every few hours depending on your
                operational needs. Use this field to customize the spacing between each activation
                cycle. The system will re-trigger the event at the defined interval as long as the
                condition remains true and the Global Action is active. Valid input range: 30 to
                7200 seconds (i.e., from every 30 seconds to every 2 hours).
              </p>
            </div>
          </button>

          <div className="w-25">
            <TextToggle
              options={['Off', 'On']}
              value={device[value] !== undefined && device[value] !== disableValue}
              setValue={(_value) => {
                if (_value)
                  setData({
                    ...device,
                    [value]: minValue || 0,
                  });
                else
                  setData({
                    ...device,
                    [value]: disableValue || 0,
                  });
              }}
              invert
            />
          </div>

          <div className="d-flex align-items-center gap-2">
            <input
              disabled={device[value] === disableValue}
              className="form-control"
              type="number"
              value={device[value] || 0}
              onChange={(e) =>
                setData({
                  ...device,
                  [value]: e.target.value,
                })
              }
              onBlur={() =>
                setData({
                  ...device,
                  [value]: Math.max(minValue, Math.min(maxValue, device[value])),
                })
              }
            />
            {text && <p className="m-0">{text}</p>}
          </div>
        </div>

        {component.countEnabled && (
          <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-2 mt-2">
            <div>
              <label>{component.countLabel}</label>
            </div>

            <div>
              {device[repeatValue] !== repeatForeverValue && (
                <input
                  disabled={device[value] === disableValue}
                  className="form-control"
                  type="number"
                  value={device[repeatValue] || 0}
                  onChange={(e) =>
                    setData({
                      ...device,
                      [repeatValue]: e.target.value,
                    })
                  }
                  onBlur={() =>
                    setData({
                      ...device,
                      [repeatValue]: Math.max(device[repeatValue], 1),
                    })
                  }
                />
              )}
            </div>

            <div className="d-flex gap-2">
              <input
                className="form-check-input"
                type="checkbox"
                disabled={device[value] === disableValue}
                checked={device[repeatValue] === repeatForeverValue}
                onChange={(e) =>
                  setData({
                    ...device,
                    [repeatValue]: e.target.checked ? repeatForeverValue : 0,
                  })
                }
              />
              <p className="m-0">{repeatForeverLabel}</p>
            </div>
          </div>
        )}

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderSlider = (component) => {
    if (!device?.status) return null;

    const { value, min, max } = component.props;

    // Don't call setData during render - defer it
    if (device[value] === null || device[value] === undefined) {
      Promise.resolve().then(() => {
        setData({ ...device, [value]: max }, false);
      });
    }

    return (
      <div>
        <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-4">
          <label className="mb-0 flex-shrink-0" style={{ minWidth: 96 }}>
            {component.label} {device[value]}%
          </label>
          <input
            type="range"
            className="form-range w-100"
            style={{ maxWidth: 240 }}
            value={device?.[value] ?? max}
            min={min}
            max={max}
            onChange={(e) =>
              setData({
                ...device,
                [value]: Math.min(Math.max(e.target.value, min), max),
              })
            }
          />
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderExternalShare = () => {
    //TODO: If this gets re-enabled it will need to be bootstrapped.
    return null;
  };

  const renderUnsavedChanges = () => {
    return (
      <div className={`${changes ? 'd-block' : 'd-none'}`}>
        *You have unsaved changes, Discard or Save to continue*
      </div>
    );
  };

  const renderSaveButton = () => {
    return (
      <div className="d-flex flex-fill justify-content-end">
        {sending && (
          <div>
            <WidgetLoadingAnimation disableText />
          </div>
        )}
        {errorMsg && <p className="text-danger">{errorMsg}</p>}
        {successMsg && <p className="text-success">{successMsg}</p>}
        <Button
          className="p-3"
          disabled={
            device?.data?.[0]?.status < 0 ||
            (device?.kill_switch && !device?.maintenanceOverride) ||
            (device?.audioIndex < 0 && !audioOverride) ||
            sending
          }
          onClick={() => on('submit', device)}
        >
          Save
        </Button>
      </div>
    );
  };

  // Helper function for renderAudioDropdown
  const updateSelectedAudio = (evt) => {
    const value = evt.target.value;
    const list = audioOverride ? [audioOverride, ...audioList] : audioList;

    if (value === -1) {
      setData({
        ...device,
        status: list?.[value]?.relay_id || true,
        data: [{ ...(device?.data?.[0] || {}), status: 1 }],
        audioIndex: -1,
        audio: null,
      });
    } else {
      setData({
        ...device,
        status: list?.[value]?.relay_id || true,
        data: [{ ...(device?.data?.[0] || {}), status: 1 }],
        audioIndex: value || -1,
        audio: list?.[value],
      });
    }
  };

  const renderAudioDropdown = () => {
    if (!device?.status) return null;
    if (!audioList || audioList.length === 0) return null;
    const list = audioOverride ? [audioOverride, ...audioList] : audioList;

    return (
      <div>
        <div className="d-flex flex-column flex-lg-row gap-2">
          <select
            className="form-select"
            onChange={updateSelectedAudio}
            value={device?.audioIndex ?? -1}
          >
            <option value="-1">-- Select Audio --</option>
            {list.map((audio, index) => (
              <option key={audio?.id || index} value={index}>
                {audio.displayName}
              </option>
            ))}
          </select>

          {/* TODO: Re write AudioPreview component */}
          {device?.audio?.id && <AudioPreview tone={device?.audio} />}

          {audioManagerButtonEnabled &&
            (perms.testPermission([], ['CreateSirenAudioFiles']) ||
              perms.testPermission([], ['Edit-DeleteSirenAudioFiles'])) &&
            device?.camera && (
              <Button
                className="p-2 px-4"
                onClick={() => {
                  on('audio-manager', {});
                  stopLiveAudio();
                }}
              >
                <PiPencilLineFill />
              </Button>
            )}
        </div>

        <hr className="d-block d-lg-none" />
      </div>
    );
  };

  const renderMaintenanceOverride = () => {
    if (perms.isMaintenanceOperator() && (!device?.kill_switch || device?.data?.[0]?.status > -1))
      return (
        <div className="d-flex gap-2 align-items-baseline">
          <input
            className="form-check-input"
            type="checkbox"
            checked={!!device?.maintenanceOverride}
            onChange={(e) => {
              setData({ ...device, maintenanceOverride: e.target.checked });
            }}
          />
          <p>
            Override <span className="text-warning">Maintenance</span>?
          </p>
        </div>
      );
  };

  const renderDisplayMessage = () => {
    if (displayMessage)
      return (
        <p className={displayMessage?.type === 'error' ? 'text-error' : 'text-success'}>
          {displayMessage.message}
        </p>
      );
  };

  if (!device && !devices) return null;

  return (
    <div className="container-fluid d-flex flex-column gap-2">
      {layout[type.toLowerCase()]?.components?.map((component, index) => (
        <div key={`${component.type}-${index}`}>{renderComponent(component)}</div>
      ))}
      <audio className="hidden" ref={audioRef} autoPlay />
    </div>
  );
}
